'use client'
import { useEffect, useRef, useState } from 'react'
import useSWRInfinite from 'swr/infinite'
import type { Offer } from '@/utils/offers'
import { formatNatAmount, natAmount } from '@/utils/nat-offers'
import { readNatTransferPage } from '@/utils/nat-transfer-candidates'
import Wallet from '@/utils/wallet'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import event from '@/utils/event'
import OfferDialog from './offer-dialog'
import ReviewOfferModal from './review-offer-modal'
import OfferUsd from './offer-usd'
import styles from './trade-panel.module.css'

export default function NatInstantSell({
	offers,
	initialOffer,
	onClose,
	onChanged
}: {
	offers: Offer[]
	initialOffer?: string
	onClose: () => void
	onChanged: () => void
}) {
	const loggedIn = useLoggedIn()
	const address = loggedIn ? Wallet.getTaprootAddress() : ''
	const [selectedId, setSelectedId] = useState(initialOffer || '')
	const [transfer, setTransfer] = useState('')
	const [manual, setManual] = useState('')
	const [review, setReview] = useState(false)
	useEffect(() => {
		setTransfer('')
		setManual('')
		setReview(false)
	}, [address])
	const sentinel = useRef<HTMLDivElement>(null)
	const { data, error, isLoading, isValidating, size, setSize, mutate } = useSWRInfinite(
		(page, previous) =>
			address && (page === 0 || previous?.more) ? ['nat:transfer-candidates', address, page] : null,
		([, owner, page]) => readNatTransferPage(owner as string, page as number),
		{ revalidateOnFocus: true, shouldRetryOnError: false }
	)
	const candidates = [
		...new Map((data || []).flatMap((p) => p.candidates).map((c) => [c.id, c])).values()
	]
	const available = offers.filter(
		(o) =>
			o.nat_terms &&
			o.buyer_address !== address &&
			o.buyer_payment_address !== address &&
			o.state === 'active' &&
			Date.parse(o.expires_at) > Date.now()
	)
	const selected = available.find((o) => o.id === selectedId)
	const matching = candidates.filter(
		(c) => selected && natAmount(c.amount) === natAmount(selected.nat_terms!.token_amount)
	)
	const itemId = matching.some((c) => c.id === transfer)
		? transfer
		: /^[a-f\d]{64}i\d+$/.test(manual)
		? manual
		: matching.length === 1
		? matching[0].id
		: ''
	useEffect(() => {
		const node = sentinel.current
		if (!node || !data?.at(-1)?.more || error || isValidating) return
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) void setSize(size + 1)
			},
			{ rootMargin: '150px' }
		)
		observer.observe(node)
		return () => observer.disconnect()
	}, [data, error, isValidating, setSize, size, selectedId])
	if (review && selected && itemId)
		return (
			<ReviewOfferModal offer={selected} itemId={itemId} onClose={onClose} onChanged={onChanged} />
		)
	return (
		<OfferDialog
			title="Sell DMT-NAT"
			description="Choose a funded offer and a matching transfer from your wallet."
			onClose={onClose}
			footer={
				<button
					className={styles.primary}
					disabled={!!address && (!selected || !itemId || !!error || isValidating)}
					onClick={() => {
						if (!address) {
							onClose()
							event.emit('command', { id: 'connect-wallet' })
							return
						}
						setReview(true)
					}}
				>
					{address ? 'Review sale' : 'Connect wallet'}
				</button>
			}
		>
			<div className={styles.sectionTitle}>
				Available offers <span>One exact lot per fill</span>
			</div>
			{!available.length && <p className={styles.empty}>No active offers available to sell into.</p>}
			<div className={styles.rows}>
				{available.map((o) => (
					<button
						key={o.id}
						className={styles.selectRow}
						aria-pressed={selectedId === o.id}
						onClick={() => {
							setSelectedId(o.id)
							setTransfer('')
							setManual('')
						}}
					>
						<span>
							<strong>{formatNatAmount(o.nat_terms!.token_amount)} NAT</strong>
							<small>{o.nat_terms!.sats_per_nat} sats/NAT</small>
						</span>
						<span>
							<strong>{(o.price_sats / 1e8).toFixed(8)} BTC</strong>
							<OfferUsd sats={o.price_sats} />
						</span>
					</button>
				))}
			</div>
			{selected && (
				<>
					<div className={styles.sectionTitle}>
						Transfer lot{' '}
						<button className={styles.textButton} onClick={() => void mutate()}>
							Refresh
						</button>
					</div>
					{!address ? (
						<p className={styles.empty}>Connect your wallet to find matching transfers.</p>
					) : isLoading ? (
						<p role="status">Finding your NAT transfers…</p>
					) : error ? (
						<p role="alert">
							Transfer inventory unavailable.{' '}
							<button className={styles.textButton} onClick={() => void mutate()}>
								Retry
							</button>
						</p>
					) : (
						<>
							{matching.map((c) => (
								<button
									key={c.id}
									className={styles.selectRow}
									aria-pressed={itemId === c.id}
									onClick={() => {
										setTransfer(c.id)
										setManual('')
									}}
								>
									<span className={styles.inscription}>
										{c.id.slice(0, 12)}…{c.id.slice(-6)}
									</span>
									<strong>{formatNatAmount(c.amount)} NAT</strong>
								</button>
							))}
							{!matching.length && (
								<p className={styles.empty}>
									{data?.at(-1)?.more
										? 'Checking your remaining transfers. This offer needs exactly '
										: 'No matching eligible transfer found. This offer needs exactly '}
									{formatNatAmount(selected.nat_terms!.token_amount)} NAT in one fresh transfer.
								</p>
							)}
						</>
					)}
					<div ref={sentinel} aria-live="polite">
						{isValidating && !isLoading ? 'Checking more transfers…' : ''}
					</div>
					<details className={styles.advanced}>
						<summary>Use a transfer inscription ID</summary>
						<label htmlFor="nat-transfer-id">Transfer inscription</label>
						<input
							id="nat-transfer-id"
							value={manual}
							placeholder="Inscription ID"
							onChange={(e) => {
								setManual(e.target.value.trim())
								setTransfer('')
							}}
						/>
					</details>
					<p className={styles.note}>
						Use a fresh transfer never signed for sale elsewhere. OW checks the token, amount, and
						ownership before signing, then confirms indexed NAT delivery after settlement.
					</p>
					<div className={styles.quote}>
						<h3>Sale preview</h3>
						<dl>
							<div>
								<dt>You sell</dt>
								<dd>{formatNatAmount(selected.nat_terms!.token_amount)} NAT</dd>
							</div>
							<div>
								<dt>Price</dt>
								<dd>{selected.nat_terms!.sats_per_nat} sats/NAT</dd>
							</div>
							<div>
								<dt>You receive</dt>
								<dd>
									{(selected.price_sats / 1e8).toFixed(8)} BTC{' '}
									<OfferUsd sats={selected.price_sats} block />
								</dd>
							</div>
							<div>
								<dt>Settlement fees</dt>
								<dd>Funded by buyer</dd>
							</div>
						</dl>
					</div>
				</>
			)}
		</OfferDialog>
	)
}
